import React, { Fragment, useState } from 'react';
import {
  Button,
  IconButton,
  Option,
  Select,
  Typography,
} from '@material-tailwind/react';
import { MinusIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  BoostUpTypes,
  ChosenVariant,
  ChosenVariantDto,
  CourseInfoType,
  ShopInfoProps,
} from '../../constants/types';
import { numberWithCommas } from '../../api/utils/format/numbers';
import { useSearchParams } from 'react-router-dom';
import {
  isProductPurchasable,
  PRODUCT_INACTIVE_LABEL,
  PRODUCT_INACTIVE_MESSAGE,
} from '../../utils/product-active';

interface OptionsItemProps {
  gubun: string;
  shopInfo: ShopInfoProps | CourseInfoType | BoostUpTypes;
  chosenVariants: ChosenVariant[];
  closeDrawerBottom: any;
  onCartHandle: any;
  onPurchaseHandle: any;
  isPointProduct: boolean;
}

const parseOptionItems = (
  searchParams: URLSearchParams,
): ChosenVariantDto[] => {
  const itemsString = searchParams.get('optionItems');
  if (!itemsString) return [];
  return JSON.parse(itemsString);
};

const stringifyOptionItems = (items: ChosenVariantDto[]): string => {
  return JSON.stringify(items);
};

export const Options = ({
  gubun,
  shopInfo,
  chosenVariants,
  closeDrawerBottom,
  onCartHandle,
  onPurchaseHandle,
  isPointProduct,
}: OptionsItemProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCartLoading, setIsCartLoading] = useState(false);
  const [isPurchaseLoading, setIsPurchaseLoading] = useState(false);
  const optionItem = parseOptionItems(searchParams);
  const totalQuantity = optionItem.reduce((acc, cur) => acc + cur.quantity, 0);

  // 구매 최소 수량: 포인트 쿠폰 최소 수량 2개 고정, 옵션 상품의 min_quantity, 없으면 base_quantity(기본수량)
  const minQuantity = (() => {
    // 포인트 상품인 경우 구매 가능한 최소 수량 2개로 고정
    if (shopInfo.is_point_product) {
      return 2;
    }

    try {
      return shopInfo.option_products[0].option_constraints[0].min_quantity;
    } catch {
      return shopInfo.base_quantity;
    }
  })();

  // 구매 최대 수량: 포인트 쿠폰 최대 20개까지 선택 가능
  const maxQuantity = (() => {
    if (shopInfo.is_point_product) {
      return 20;
    }
    return Infinity;
  })();

  // 수량 증감 단위: 기본수량, 값이 없으면 1개씩 증감
  const stepQuantity = shopInfo.base_quantity || 1;

  // 옵션을 처음 선택했을 때 담기는 수량: 포인트 쿠폰은 최소 수량보다 적게 담기지 않도록 보정
  const initialQuantity = shopInfo.is_point_product
    ? Math.max(stepQuantity, minQuantity)
    : stepQuantity;

  /** 옵션 헤더에 노출할 수량 제한 안내 문구 (제한이 있는 항목만 표기) */
  const quantityLimitLabel = [
    minQuantity > 1 && `최소 수량 ${minQuantity}개`,
    Number.isFinite(maxQuantity) && `최대 수량 ${maxQuantity}개`,
  ]
    .filter(Boolean)
    .join(' / ');

  const getOptionPrice = (option: ChosenVariant) => {
    switch (gubun) {
      case 'boostup':
      case 'shop':
        return shopInfo.current_price + option.additional_price;
      case 'course':
        return (
          shopInfo.option_products?.find(
            (v1) => v1.option_product.id === option.product_id,
          ).option_product.current_price + option.additional_price
        );
      default:
        return 0;
    }
  };

  const selectOption = (str: string) => {
    const option = chosenVariants.find((el) => el.id === str);

    if (!option) return;

    const existingItemIndex = optionItem.findIndex(
      (el) => el.variant.id === str,
    );

    if (existingItemIndex >= 0) {
      const updatedItems = [...optionItem];
      const existingItem = updatedItems[existingItemIndex];
      const nextQuantity = existingItem.quantity + existingItem.baseQuantity;

      // 최대 수량 제한 상품(포인트 상품 등)은 상한 초과 시 증가 차단
      if (nextQuantity > maxQuantity) {
        alert(`최대 ${maxQuantity}개까지 선택 가능합니다.`);
        return;
      }

      updatedItems[existingItemIndex] = {
        ...existingItem,
        quantity: nextQuantity,
        totalPrice: nextQuantity * getOptionPrice(option),
      };
      searchParams.set('optionItems', stringifyOptionItems(updatedItems));
      setSearchParams(searchParams);
    } else {
      const newData: ChosenVariantDto = {
        baseQuantity: stepQuantity,
        quantity: initialQuantity,
        totalPrice: initialQuantity * getOptionPrice(option),
        variant: { ...option },
      };

      searchParams.set(
        'optionItems',
        stringifyOptionItems([...optionItem, newData]),
      );
      setSearchParams(searchParams);
    }
  };

  const quantityHandler = (idx: number, delta: number) => {
    const updatedItems = optionItem.map((item, index) =>
      index === idx
        ? {
            ...item,
            quantity: item.quantity + delta,
            totalPrice: (item.quantity + delta) * getOptionPrice(item.variant),
          }
        : item,
    );

    searchParams.set('optionItems', stringifyOptionItems(updatedItems));
    setSearchParams(searchParams);
  };

  const onRemoveItem = (idx: number) => {
    const modifiedArray = [
      ...optionItem.slice(0, idx),
      ...optionItem.slice(idx + 1),
    ];

    if (modifiedArray.length === 0) {
      searchParams.delete('optionItems');
    } else {
      searchParams.set('optionItems', stringifyOptionItems(modifiedArray));
    }
    setSearchParams(searchParams);
  };

  /** 판매 중지(is_active === false) 상품이면 장바구니/구매 불가 */
  const isPurchasable = isProductPurchasable(shopInfo);

  /**
   * 장바구니 버튼 이벤트
   * - 1. 판매 가능 여부 체크
   * - 2. 버튼 로딩 상태 변경
   * - 3. 장바구니 추가 이벤트 실행
   */
  const eventCartHandle = async () => {
    if (!isPurchasable) {
      alert(PRODUCT_INACTIVE_MESSAGE);
      return;
    }
    setIsCartLoading(true);
    try {
      await onCartHandle(shopInfo.id, optionItem);
    } finally {
      setIsCartLoading(false);
    }
  };

  /**
   * 구매하기 버튼 이벤트
   * - 1. 판매 가능 여부 체크
   * - 2. 최소 수량 체크
   * - 3. 버튼 로딩 상태 변경
   * - 4. 구매하기 이벤트 실행
   */
  const eventPurchaseHandle = async () => {
    if (!isPurchasable) {
      alert(PRODUCT_INACTIVE_MESSAGE);
      return;
    }
    if (minQuantity > totalQuantity) {
      alert('최소 옵션은 ' + minQuantity + '개 이상 선택해주세요.');
      return;
    }
    setIsPurchaseLoading(true);
    try {
      await onPurchaseHandle(shopInfo.id, optionItem);
    } finally {
      setIsPurchaseLoading(false);
    }
  };

  let buttonDisabled: boolean;
  if (!isPurchasable) {
    buttonDisabled = true;
  } else if (shopInfo.option_products) {
    buttonDisabled = false;
  } else {
    buttonDisabled = optionItem.length === 0;
  }

  return (
    <Fragment>
      <section>
        <div className="mb-2.5 flex flex-row items-center gap-8">
          <div className="items-left flex w-full flex-col border-b pb-1 text-sm md:flex-row md:items-center md:text-base">
            <Typography
              variant="h5"
              className="self-start py-2.5 text-sm font-bold md:text-base"
            >
              옵션 선택 {quantityLimitLabel && ` : ${quantityLimitLabel}`}
            </Typography>
          </div>
          <div className="ml-auto flex items-center">
            <IconButton
              variant="text"
              color="blue-gray"
              onClick={() => closeDrawerBottom()}
            >
              <XMarkIcon className="!h-[30px] !w-[30px]" />
            </IconButton>
          </div>
        </div>
      </section>
      <section className="max-h-80 min-h-60 overflow-y-auto">
        {shopInfo && (
          <div className="mb-2.5 mt-1 flex flex-col">
            <Select
              label="옵션 선택"
              className="w-full overflow-hidden [&>span:first-of-type]:left-3 [&>span:first-of-type]:right-8 [&>span:first-of-type]:truncate"
              containerProps={{ className: 'w-full' }}
              selected={(element) => {
                const name = chosenVariants?.find(
                  (v) => v.id === element?.props?.value,
                )?.name;
                return (
                  <span className="block truncate" title={name}>
                    {name}
                  </span>
                );
              }}
              onChange={(e) => selectOption(e)}
            >
              {chosenVariants &&
                chosenVariants.map((item) => (
                  <Option
                    className="whitespace-normal p-2"
                    value={item.id}
                    key={item.id}
                  >
                    <Typography className="whitespace-normal break-words">
                      {item.name}
                    </Typography>
                  </Option>
                ))}
            </Select>
            {gubun !== 'shop' && shopInfo.option_products.length > 0 && (
              <div className="mt-5 flex justify-between">
                <div>{shopInfo.name}</div>
                <div>{numberWithCommas(shopInfo.current_price)} 원</div>
              </div>
            )}
            {optionItem &&
              optionItem.map((item, index: number) => (
                <div key={index} className="mx-4 my-1 flex flex-col">
                  <div className="flex flex-row items-center gap-4">
                    <Typography
                      variant="h6"
                      color="blue-gray"
                      className="font-normal"
                    >
                      {item.variant?.name}
                    </Typography>
                    <div className="ml-auto flex items-center">
                      <IconButton
                        variant="text"
                        color="blue-gray"
                        onClick={() => onRemoveItem(index)}
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </IconButton>
                    </div>
                  </div>
                  <div className="flex flex-row items-center gap-2 px-2 py-2">
                    <Button
                      variant="outlined"
                      size="sm"
                      onClick={() => quantityHandler(index, -item.baseQuantity)}
                      disabled={
                        item.quantity - item.baseQuantity < initialQuantity
                      }
                    >
                      <MinusIcon className="h-2 w-2" />
                    </Button>

                    <Typography>{item.quantity}</Typography>

                    <Button
                      variant="outlined"
                      size="sm"
                      onClick={() => quantityHandler(index, item.baseQuantity)}
                      disabled={item.quantity + item.baseQuantity > maxQuantity}
                    >
                      <PlusIcon className="h-2 w-2" />
                    </Button>
                    <div className="ml-auto flex items-center gap-2">
                      <Typography className="items-center font-medium">
                        {numberWithCommas(item.totalPrice)}
                      </Typography>
                      <Typography className="items-center font-medium">
                        원
                      </Typography>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </section>
      <section>
        <div className="flex justify-between border-t py-2">
          <div>총가격</div>
          <div>
            {numberWithCommas(
              optionItem.reduce<number>((acc, cur) => acc + cur.totalPrice, 0) +
                (shopInfo.option_products?.length > 0
                  ? shopInfo.current_price
                  : 0),
            )}{' '}
            원
          </div>
        </div>
        {!isPurchasable && (
          <div className="pb-2 text-center text-sm text-red-500">
            {PRODUCT_INACTIVE_MESSAGE}
          </div>
        )}
        <div className="flex w-full justify-center gap-4 rounded-md">
          {gubun !== 'course' && !isPointProduct && (
            <Button
              disabled={buttonDisabled || isCartLoading}
              className="flex w-full flex-row items-center justify-center gap-2 rounded-[4px] border border-purple5 bg-white text-purple5 shadow-none hover:bg-purple0 hover:shadow-none"
              onClick={eventCartHandle}
            >
              {isCartLoading && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-purple5 border-t-transparent" />
              )}
              장바구니
            </Button>
          )}
          <Button
            disabled={buttonDisabled || isPurchaseLoading}
            className="flex w-full flex-row items-center justify-center gap-2 rounded-[4px] bg-purple5 shadow-none hover:bg-purple6 hover:shadow-none"
            onClick={eventPurchaseHandle}
          >
            {isPurchaseLoading && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            {isPurchasable ? '구매하기' : PRODUCT_INACTIVE_LABEL}
          </Button>
        </div>
      </section>
    </Fragment>
  );
};
